import React, { useState, useEffect } from 'react';
import PortfolioDemo from './demos/PortfolioDemo';
import ScreenerDemo from './demos/ScreenerDemo';
import AlphaChatDemo from './demos/AlphaChatDemo';
import MarketTrendsDemo from './demos/MarketTrendsDemo';
import './Onboarding.css';

// First-visit guided site tour. Shown once (localStorage-gated) after a user
// first lands logged-in; re-openable anytime via the window 'alphagrade:open-tour'
// event (the footer "Take the tour" link dispatches it). Slides walk through
// each app page high-level; the ones that benefit embed a small animated demo
// (the same code-driven demos used on the marketing page — no video).
const STEPS = [
  {
    icon: 'AG',
    title: 'Welcome to AlphaGrade',
    description:
      'Institutional-grade analysis and an AI research partner, built for investors who manage their own money. Here’s a 60-second tour of what you can do.',
  },
  {
    icon: '🔍',
    title: 'Screen any stock',
    description:
      'Type any tickers and AlphaGrade scores them across Quality, Setup, Convex and Macro — with the test-by-test breakdown behind every grade.',
    Demo: ScreenerDemo,
  },
  {
    icon: '💼',
    title: 'Build & grade your portfolio',
    description:
      'Build your portfolio right in the app, then grade every position and see where your real risk and conviction actually sit — no spreadsheets required.',
    Demo: PortfolioDemo,
  },
  {
    icon: '💬',
    title: 'Talk to Alpha',
    description:
      'Alpha is your AI analyst. Ask about any stock or your whole portfolio and it researches live data — estimate revisions, institutional flow, news — then answers in plain English.',
    Demo: AlphaChatDemo,
  },
  {
    icon: '⭐',
    title: 'Save ideas to watchlists',
    description:
      'Group tickers into named watchlists to track ideas — or just ask Alpha to build one for you: “save these to a watchlist called AI Power.”',
  },
  {
    icon: '🔔',
    title: 'Never miss a grade change',
    description:
      'Watch any stock with Grade Watch and get an email the moment its grade changes — with the reason it moved. It runs automatically every night.',
  },
  {
    icon: '📊',
    title: 'Read the whole market',
    description:
      'Check sector strength, the day’s movers, and the current market regime so you always know the backdrop before you act.',
    Demo: MarketTrendsDemo,
  },
  {
    icon: '🚀',
    title: 'You’re all set',
    description:
      'That’s the tour. Everything lives in the menu at the top-left — jump into the Screener or build a portfolio to get started. You can replay this tour anytime from the footer.',
  },
];

export default function Onboarding() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem('alphagrade_onboarding_complete');
    if (done) return;
    // Signed-in visitors only, which is what the comment above always claimed
    // and the code never actually checked. A logged-out visitor is usually an
    // ad click, and this fired over the top of the free grader on `/` — the one
    // thing the whole landing-page rework existed to put in front of them. A
    // stranger who has been on the site for one second is not going to accept
    // an offer of a 60-second tour; they need to see the product work first.
    // Anyone who does want it still has "Take a tour" in the top bar and
    // "Take the tour" in the footer, both of which fire alphagrade:open-tour.
    if (!localStorage.getItem('alphagrade_auth_token')) return;
    setVisible(true);
  }, []);

  // Allow replaying the tour on demand (footer "Take the tour" link).
  useEffect(() => {
    const openTour = () => { setStep(0); setVisible(true); };
    window.addEventListener('alphagrade:open-tour', openTour);
    return () => window.removeEventListener('alphagrade:open-tour', openTour);
  }, []);

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else complete();
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  const complete = () => {
    localStorage.setItem('alphagrade_onboarding_complete', 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const DemoComp = current.Demo;

  return (
    <div className="onboarding-overlay">
      <div className={`onboarding-card ${DemoComp ? 'has-demo' : ''}`}>
        <div className="onboarding-icon">{current.icon}</div>
        <h2 className="onboarding-title">{current.title}</h2>
        <p className="onboarding-description">{current.description}</p>

        {DemoComp && (
          <div className="onboarding-demo">
            <DemoComp key={step} autoPlay loop />
          </div>
        )}

        <div className="onboarding-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
              onClick={() => setStep(i)}
              role="button"
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        <div className="onboarding-actions">
          {step > 0 && (
            <button className="onboarding-skip" onClick={handleBack}>Back</button>
          )}
          <button className="onboarding-skip" onClick={complete}>Skip</button>
          <button className="onboarding-next" onClick={handleNext}>
            {step < STEPS.length - 1 ? 'Next' : 'Get Started'}
          </button>
        </div>
      </div>
    </div>
  );
}
